import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { apiClient } from "src/dm/utils/axios";
import { APIS } from "src/dm/utils/constants";
import { DealReward } from "src/dm/interfaces/DealReward";
import {
  DealRewardAvailableFamily,
  DealRewardBilling,
  DealRewardBillingWriteRequest,
  DealRewardFamily,
  DealRewardFamilyWriteRequest,
  DealRewardItem,
  DealRewardItemGroup,
  DealRewardItemGroupWriteRequest,
  DealRewardItemWriteRequest,
  DealRewardWriteRequest,
} from "src/dm/interfaces/DealRewardSub";

interface Envelope<T> {
  items: T[];
  total: number;
}

// All endpoints below key off `dealId` only — DealReward is 1:1 with
// Deal (legacy DealReward.GetByDealId returns a single object). The
// resolved DEAL_RWRD_ID never leaks into URLs the SPA constructs.
const rewardUrl = (dealId: number) => `${APIS.Deals}/${dealId}/reward`;
const subUrl = (dealId: number, suffix: string) =>
  `${rewardUrl(dealId)}/${suffix}`;

// ---- Reward defaults ------------------------------------------------------

/**
 * Brand-new reward defaults derived from the deal's category manager via
 * DEAL_MAPPINGS. Mirrors the legacy `EditReward.aspx.cs.setDefaltSelection`
 * which seeded `ddlCategoryManager`, `ddlBusUnit`, `ddlEntryType`,
 * `ddlSalesPrsn` when a brand-new reward is created. Returns null fields
 * when the deal has no category manager or no mapping row.
 */
export interface RewardDefaults {
  dealId: number;
  categoryManagerCode: number | null;
  ownerUserId: string | null;
  supportTeamMember: string | null;
  rewardEntryType: string | null;
  businessUnit: string | null;
}

export const useRewardDefaults = (dealId: number | undefined) =>
  useQuery({
    queryKey: ["reward-defaults", dealId],
    enabled: dealId !== undefined && dealId > 0,
    queryFn: async (): Promise<RewardDefaults> => {
      const response = await apiClient.get<RewardDefaults>(
        `${rewardUrl(dealId as number)}/defaults`
      );
      return response.data;
    },
    staleTime: 60 * 1000,
  });

// Cascade "promote every item on this reward to mandatory (ITM_TYP_CD=M)".
// Legacy EditReward / EditRewardItems calls this when the parent reward
// becomes mandatory; the SPA exposes it as a button on EditReward.
export const usePromoteRewardItemsToMandatory = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ promoted: number }> => {
      const response = await apiClient.post<{ promoted: number }>(
        `${rewardUrl(dealId)}/promote-items`,
        {}
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reward-items", dealId] });
    },
  });
};

// ---- Reward header (single) -----------------------------------------------

/**
 * Read the deal's reward header (singular — DealReward is 1:1). Returns
 * `null` when the deal has no reward yet (404 → null). Keep `useDealReward`
 * in `src/hooks/useDealRewards.ts` as the canonical entry-point; this
 * re-export is here for the few writers that want the mutation hooks in
 * the same import statement.
 */
export { useDealReward } from "src/dm/hooks/useDealRewards";

/**
 * Create the deal's reward. Throws 409 if one already exists (DealReward is
 * 1:1 — use PUT instead). The SPA surfaces this as a "Create Reward" CTA on
 * the reward page when the GET returns null.
 */
export const useAddDealReward = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: DealRewardWriteRequest): Promise<DealReward> => {
      const response = await apiClient.post<DealReward>(
        rewardUrl(dealId),
        body
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-reward", dealId] });
    },
  });
};

export const useUpdateDealReward = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: DealRewardWriteRequest): Promise<DealReward> => {
      const response = await apiClient.put<DealReward>(rewardUrl(dealId), body);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-reward", dealId] });
    },
  });
};

export const useDeleteDealReward = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await apiClient.delete(rewardUrl(dealId));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-reward", dealId] });
    },
  });
};

// ---- Reward items ---------------------------------------------------------
//
// All sub-aggregate hooks key off dealId only. The server resolves the
// (single) reward id internally and 404s if no reward exists yet — that's
// expected when the parent reward has been deleted; the SPA branches on
// the parent-reward GET first and never invokes these hooks against a
// reward-less deal.

const fetchEnvelopeOrEmpty = async <T>(url: string): Promise<T[]> => {
  try {
    const response = await apiClient.get<Envelope<T>>(url);
    return response.data.items;
  } catch (err) {
    // 404 ⇒ no reward exists yet ⇒ no sub-rows. Return [] so the SPA
    // grids render an empty state instead of bubbling a noisy error.
    if (err instanceof AxiosError && err.response?.status === 404) {
      return [];
    }
    throw err;
  }
};

export const useRewardItems = (dealId: number | undefined) =>
  useQuery({
    queryKey: ["reward-items", dealId],
    enabled: dealId !== undefined && dealId > 0,
    queryFn: (): Promise<DealRewardItem[]> =>
      fetchEnvelopeOrEmpty<DealRewardItem>(
        subUrl(dealId as number, "items")
      ),
  });

export const useAddRewardItem = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: DealRewardItemWriteRequest
    ): Promise<DealRewardItem> => {
      const response = await apiClient.post<DealRewardItem>(
        subUrl(dealId, "items"),
        body
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reward-items", dealId] });
    },
  });
};

export const useDeleteRewardItem = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rewardItemId: number): Promise<void> => {
      await apiClient.delete(`${subUrl(dealId, "items")}/${rewardItemId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reward-items", dealId] });
    },
  });
};

// ---- Reward item groups ---------------------------------------------------

export const useRewardItemGroups = (dealId: number | undefined) =>
  useQuery({
    queryKey: ["reward-item-groups", dealId],
    enabled: dealId !== undefined && dealId > 0,
    queryFn: (): Promise<DealRewardItemGroup[]> =>
      fetchEnvelopeOrEmpty<DealRewardItemGroup>(
        subUrl(dealId as number, "item-groups")
      ),
  });

export const useAddRewardItemGroup = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: DealRewardItemGroupWriteRequest
    ): Promise<DealRewardItemGroup> => {
      const response = await apiClient.post<DealRewardItemGroup>(
        subUrl(dealId, "item-groups"),
        body
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reward-item-groups", dealId] });
    },
  });
};

export const useDeleteRewardItemGroup = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      itemType: string;
      rewardItemGroupId: number;
    }): Promise<void> => {
      await apiClient.delete(
        `${subUrl(dealId, "item-groups")}/${args.itemType}/${args.rewardItemGroupId}`
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reward-item-groups", dealId] });
    },
  });
};

// ---- Reward families ------------------------------------------------------

export const useRewardFamilies = (dealId: number | undefined) =>
  useQuery({
    queryKey: ["reward-families", dealId],
    enabled: dealId !== undefined && dealId > 0,
    queryFn: (): Promise<DealRewardFamily[]> =>
      fetchEnvelopeOrEmpty<DealRewardFamily>(
        subUrl(dealId as number, "families")
      ),
  });

export const useAddRewardFamily = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: DealRewardFamilyWriteRequest
    ): Promise<DealRewardFamily> => {
      const response = await apiClient.post<DealRewardFamily>(
        subUrl(dealId, "families"),
        body
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reward-families", dealId] });
      qc.invalidateQueries({
        queryKey: ["reward-available-families", dealId],
      });
    },
  });
};

export const useDeleteRewardFamily = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      itemType: string;
      familyCode: number;
    }): Promise<void> => {
      await apiClient.delete(
        `${subUrl(dealId, "families")}/${args.itemType}/${args.familyCode}`
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reward-families", dealId] });
      qc.invalidateQueries({
        queryKey: ["reward-available-families", dealId],
      });
    },
  });
};

// ---- Available families (read-only view) ----------------------------------

export const useRewardAvailableFamilies = (dealId: number | undefined) =>
  useQuery({
    queryKey: ["reward-available-families", dealId],
    enabled: dealId !== undefined && dealId > 0,
    queryFn: (): Promise<DealRewardAvailableFamily[]> =>
      fetchEnvelopeOrEmpty<DealRewardAvailableFamily>(
        subUrl(dealId as number, "available-families")
      ),
  });

// ---- Reward billing -------------------------------------------------------

export const useRewardBilling = (dealId: number | undefined) =>
  useQuery({
    queryKey: ["reward-billing", dealId],
    enabled: dealId !== undefined && dealId > 0,
    queryFn: (): Promise<DealRewardBilling[]> =>
      fetchEnvelopeOrEmpty<DealRewardBilling>(
        subUrl(dealId as number, "billing")
      ),
  });

export const useAddRewardBilling = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: DealRewardBillingWriteRequest
    ): Promise<DealRewardBilling> => {
      const response = await apiClient.post<DealRewardBilling>(
        subUrl(dealId, "billing"),
        body
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reward-billing", dealId] });
    },
  });
};

export const useUpdateRewardBilling = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      rewardBillingId: number;
      body: DealRewardBillingWriteRequest;
    }): Promise<DealRewardBilling> => {
      const response = await apiClient.put<DealRewardBilling>(
        `${subUrl(dealId, "billing")}/${args.rewardBillingId}`,
        args.body
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reward-billing", dealId] });
    },
  });
};

export const useDeleteRewardBilling = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rewardBillingId: number): Promise<void> => {
      await apiClient.delete(
        `${subUrl(dealId, "billing")}/${rewardBillingId}`
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reward-billing", dealId] });
    },
  });
};
